import React, { useEffect, useState } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useHistory, useParams } from 'react-router-dom';
import { getEmpNoteById, getEmployeeNoteAttachmentUrl } from 'core/API/logbook';
import { useQuery } from 'react-query';
import { formatDateToMMDDYYYY } from 'shared/utils/dates/formatDate';
import { formatNoteUser } from 'module/logBook/utils/logbookValidation';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';


const ViewEmployeeNotes = () => {
  const { noteId } = useParams<{ noteId?: string }>();
  const history = useHistory();
  
  const [noteDate, setNoteDate] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [employeeName, setEmployeeName] = useState('');
  const [noteAuthor, setNoteAuthor] = useState('');
  const [attachments, setAttachments] = useState<any[]>([]);
  const [previewImage, setPreviewImage] = useState<{ url: string; alt: string } | null>(null);

  const { data: empNoteById } = useQuery(
    ['empNoteById', noteId],
    () => getEmpNoteById(parseInt(noteId as string, 10)),
    { enabled: !!noteId }
  );

  useEffect(() => {
    if (!noteId || !empNoteById) return;
    const content = empNoteById.note || empNoteById.note_text || '';
    setNoteContent(content);
    setNoteDate(empNoteById.date || empNoteById.date_of_the_note || '');
    setEmployeeName(formatNoteUser(empNoteById.author));
    setNoteAuthor(formatNoteUser(empNoteById.assignToUser));

    if (empNoteById.attachments && Array.isArray(empNoteById.attachments)) {
      setAttachments(empNoteById.attachments);
    }
  }, [noteId, empNoteById]);

  const formatDate = (dateString: string) => {
    return formatDateToMMDDYYYY(dateString);
  };

  return (
    <Box sx={{ backgroundColor: '#f4f6fa', minHeight: '100vh', py: 3 }}>
      <Box maxWidth="1100px" mx="auto">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Button
            onClick={() => history.goBack()}
            sx={{
              backgroundColor: 'rgb(68, 99, 136)',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 500,
              px: 3,
              py: 0.75,
              '&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
            }}
          >
            Back
          </Button>
        </Box>

        <Paper elevation={1}>
          <Box
            sx={{
              p: 2,
              bgcolor: 'grey.100',
              borderBottom: 1,
              borderColor: 'divider',
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4,
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h5" fontWeight={600}>
                View Employee Note
              </Typography>
            </Box>
          </Box>
          
          <Box
            sx={{
              px: 3,
              pt: 1.5,
              pb: 0.1
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              <strong>Employee:</strong> {employeeName}
            </Typography>
            <Typography variant="subtitle2" gutterBottom>
              <strong>Author:</strong> {noteAuthor}
            </Typography>
            <Typography variant="subtitle2">
              <strong>Date:</strong> {formatDate(noteDate)}
            </Typography>
          </Box>
          
          <Box sx={{ p: 3 }}>
            <ReactQuill
              value={noteContent}
              readOnly={true}
              theme="snow"
              modules={{ toolbar: false }}
              className="quill-editor"
              style={{
                backgroundColor: 'white',
                height: '200px'
              }}
            />
          </Box>

          {attachments.length > 0 && (
            <Box sx={{ px: 3, pb: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {attachments.map((att: any, index: number) => {
                const fileName = att.fileName || `Attachment ${index + 1}`;
                const imageUrl = getEmployeeNoteAttachmentUrl(fileName);
                const isImage = typeof att.mimeType === 'string'
                  ? att.mimeType.startsWith('image/')
                  : true;

                if (!isImage || !imageUrl) return null;

                return (
                  <Box
                    key={att.id || att['@id'] || index}
                    component="img"
                    src={imageUrl}
                    alt={fileName}
                    onClick={() => setPreviewImage({ url: imageUrl, alt: fileName })}
                    sx={{
                      width: 120,
                      height: 120,
                      objectFit: 'cover',
                      borderRadius: '4px',
                      border: '1px solid #e0e0e0',
                      cursor: 'pointer',
                    }}
                  />
                );
              })}
            </Box>
          )}
        </Paper>
      </Box>

      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            onClick={() => setPreviewImage(null)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              zIndex: 1,
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
            aria-label="Close preview"
          >
            <Close />
          </IconButton>
          {previewImage && (
            <Box
              component="img"
              src={previewImage.url}
              alt={previewImage.alt}
              sx={{ width: '100%', height: 'auto', display: 'block' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ViewEmployeeNotes;